import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { useApiMutation } from '../../api/mutations';
import { ordersFixture, orderDetailFixture, refundOrderFixture } from './fixture';
import { createDebugLogger } from '../../utils/debug';

const dlog = createDebugLogger('orders');

/**
 * Normalisiert eine Order-Row in eine stable Shape.
 *
 * Tolerant gegenüber zwei häufigen Drift-Mustern:
 *  - nested user-Object (`o.user.email`) vs. flat-Felder (`o.userEmail`, `o.userId`)
 *  - items vorhanden oder nicht (Liste ohne items)
 *  - amounts als string oder number, cents oder major-units
 *
 * Liefert immer:
 *   {
 *     id, orderNumber,
 *     userId, userEmail, userDisplayName,
 *     items: [],
 *     status, provider, currency,
 *     total (number, cents wenn paidTotal numerisch klein ist sonst major),
 *     vatTotal,
 *     acceptanceAgbVersion / Privacy / Withdrawal,
 *     createdAt, paidAt,
 *     raw,
 *   }
 */
function normalizeOrder(o) {
  if (!o) return null;
  const num = (v) => {
    if (v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return {
    id: o.id,
    orderNumber: o.orderNumber ?? o.number ?? `#${o.id}`,
    userId: o.userId ?? o.user?.id ?? null,
    userEmail: o.userEmail ?? o.user?.email ?? null,
    userDisplayName: o.userDisplayName ?? o.user?.displayName ?? o.user?.name ?? null,
    items: Array.isArray(o.items) ? o.items : [],
    status: o.status ?? 'unknown',
    provider: o.provider ?? '—',
    currency: o.currency ?? 'EUR',
    total: num(o.total) ?? num(o.totalAmount) ?? num(o.amount) ?? 0,
    vatTotal: num(o.vatTotal) ?? num(o.vatAmount) ?? 0,
    acceptanceAgbVersion: o.acceptanceAgbVersion ?? null,
    acceptancePrivacyVersion: o.acceptancePrivacyVersion ?? null,
    acceptanceWithdrawalVersion: o.acceptanceWithdrawalVersion ?? null,
    createdAt: o.createdAt ?? null,
    paidAt: o.paidAt ?? null,
    raw: o,
  };
}

function normalizeOrdersList(raw) {
  if (!raw) return { orders: [], total: 0 };
  const list = raw.orders ?? raw.items ?? raw.data ?? [];
  return {
    orders: list.map(normalizeOrder).filter(Boolean),
    total: raw.total ?? raw.totalCount ?? list.length,
    raw,
  };
}

export function useOrders(params = {}) {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['orders', params, fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return normalizeOrdersList(ordersFixture(params));
      }
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v) qs.set(k, v);
      });
      const res = await fetch(`/v1/admin/orders?${qs.toString()}`);
      dlog('/v1/admin/orders raw', res);
      return normalizeOrdersList(res);
    },
  });
}

export function useOrderDetail(id) {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['order', id, fixturesMode ? 'fixture' : 'live'],
    enabled: !!id,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay(150);
        const data = orderDetailFixture(id);
        if (!data) throw new Error('order_not_found');
        return { order: normalizeOrder(data.order), raw: data };
      }
      const res = await fetch(`/v1/admin/orders/${id}`);
      dlog('/v1/admin/orders/:id raw', res);
      const order = normalizeOrder(res.order ?? res);
      return { order, raw: res };
    },
  });
}

export function useRefundOrder() {
  return useApiMutation({
    live: (fetch, { id, reason }) =>
      fetch(`/v1/admin/orders/${id}/refund`, { method: 'POST', body: { reason } }),
    fixture: ({ id }) => refundOrderFixture({ id }),
    invalidate: ['orders', 'order'],
    successMessage: (res) => `Credit-Note ${res?.creditNoteId ?? '—'} angelegt.`,
  });
}
